import { createFileRoute, Link } from "@tanstack/react-router";
import equipmentImg from "@/assets/dj-mariage-equipment.jpg";
import sparksImg from "@/assets/dj-mariage-sparks.jpg";
import { useReveal } from "@/hooks/use-reveal";
import { TldrBox } from "@/components/tldr-box";
import { metaDesc } from "@/lib/seo-description";
import { LOCAL_BUSINESS_SCHEMA } from "@/lib/schema-org";

import { ReviewsProof } from "@/components/reviews-proof";
export const Route = createFileRoute("/sono-lumiere")({
  head: () => ({
    meta: [
      { title: "Éclairage scénique & effets mariage | DJ Val" },
      {
        name: "description",
        content:
          metaDesc("Éclairage scénique LED, machines à effets, étincelles froides et neige pour mariage dans le Vaucluse. Livraison et installation incluses, devis gratuit en 24 h.")
      },
      { property: "og:title", content: "Éclairage Scénique & Effets — Vaucluse / Avignon | DJ Val" },
      {
        property: "og:description",
        content:
          metaDesc("Lyres LED Chauvet, lasers, machines à fumée, tempête de neige, étincelles froides, photobooth. Installation par DJ Val.")
      },
      { property: "og:url", content: "https://dj-val.fr/sono-lumiere" },
      { property: "og:image", content: `https://dj-val.fr${equipmentImg}` },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: `https://dj-val.fr${equipmentImg}` },
    ],
    links: [{ rel: "canonical", href: "https://dj-val.fr/sono-lumiere" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Éclairage scénique & effets spéciaux — DJ Val",
          serviceType: "Éclairage scénique LED, machines à effets, étincelles froides et neige pour mariage",
          provider: { ...LOCAL_BUSINESS_SCHEMA },
          areaServed: ["Cavaillon", "Avignon", "Vaucluse", "Bouches-du-Rhône"],
        }),
      },
    ],
  }),
  component: Page,
});

const GEAR = [
  {
    title: "Systèmes de sonorisation",
    text: "Enceintes professionnelles RCF, JBL et QSC. Son clair et puissant adapté aux salles, châteaux et plein air. De 30 à 500 personnes.",
  },
  {
    title: "Éclairage scénique LED",
    text: "Projecteurs LED couleur, lyres asservies, jeux de lumière, guirlandes festives. Mise en lumière indirecte de votre lieu.",
  },
  {
    title: "Machines à effets",
    text: "Machine à fumée, fumée lourde au sol, bulles, mousse — pour des ouvertures de bal spectaculaires.",
  },
  {
    title: "Tempête de neige",
    text: "Ambiance hivernale féerique pour mariages d'hiver et événements de fin d'année.",
  },
  {
    title: "Étincelles froides & pyrotechnie",
    text: "Jets de scène et fontaines d'étincelles froides — sans danger, utilisables en intérieur.",
  },
  {
    title: "Photobooth & bar à vinyles",
    text: "Animation vintage et photobooth équipé pour des souvenirs inoubliables.",
  },
];

function Page() {
  useReveal();
  return (
    <div className="bg-background text-muted-foreground">
      <section className="on-dark relative min-h-[80vh] flex items-center justify-center text-center px-6 overflow-hidden pt-24">
        <div className="absolute inset-0 z-0">
          <img loading="lazy" decoding="async"
            src={equipmentImg}
            alt="Matériel DJ professionnel — platines, mixeur et enceintes pour mariage"
            className="h-full w-full object-cover opacity-40 kenburns-img"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(to bottom, var(--ink) 0%, transparent 35%, var(--ink) 100%)",
            }}
          />
        </div>
        <div className="relative z-10 max-w-2xl reveal space-y-6">
          <p className="eyebrow">Location Sono · Lumière · Effets</p>
          <h1 className="font-serif-display text-foreground text-4xl sm:text-5xl leading-tight">
            <span className="italic font-light">Sonorisation</span>
            <span className="block gold-text-shine mt-2">& éclairage professionnels</span>
          </h1>
          <span className="gold-rule mx-auto !w-12" />
          <p className="text-sm text-foreground/90 font-light leading-relaxed">
            Matériel haut de gamme, livré et installé par nos soins. Pour mariages,
            soirées privées et événements professionnels en Provence et Vaucluse.
          </p>
          <Link
            to="/contactez-nous"
            className="inline-block bg-[var(--gold)] text-[var(--ink)] px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all"
          >
            Demander un devis
          </Link>
        </div>
      </section>
      <ReviewsProof />

      {/* TLDR */}
      <div className="px-6">
        <TldrBox
          summary="Location de matériel pro (sonorisation, éclairage, vidéo, effets) livré et installé en Provence. Console Pioneer CDJ-3000, line-array RCF, micros Shure ULX-D, photobooth. Caution restituée intégralement après l'événement."
          items={[
            { label: "Capacité", value: "De 30 à 2 000 personnes" },
            { label: "Livraison", value: "Vaucluse, BdR, Gard (option installation)" },
            { label: "Tarif", value: "À partir de 60 € / jour (micros HF)" },
            { label: "Caution", value: "Restituée 100 % au retour du matériel" },
          ]}
        />
      </div>

      {/* GEAR */}
      <section className="py-24 px-6 max-w-7xl mx-auto">
        <div className="text-center mb-16 sr" data-reveal>
          <p className="eyebrow text-foreground">Notre matériel</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl text-foreground mt-4">
            Un parc <span className="italic text-foreground">complet</span> & professionnel
          </h2>
          <span className="gold-rule mx-auto !w-12 mt-6" />
        </div>
        <div className="grid gap-px bg-foreground/10 md:grid-cols-2 lg:grid-cols-3 border border-foreground/10">
          {GEAR.map((g, i) => (
            <div
              key={g.title}
              className="bg-background p-10 sr hover:bg-card transition-colors"
              data-reveal
              style={{ transitionDelay: `${i * 60}ms` }}
            >
              <p className="font-serif-display italic text-foreground text-2xl">
                {String(i + 1).padStart(2, "0")}
              </p>
              <h3 className="font-serif-display text-2xl text-foreground mt-4">{g.title}</h3>
              <span className="gold-rule !w-8 my-4" />
              <p className="text-sm text-muted-foreground leading-relaxed font-light">{g.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* LIVRAISON */}
      <section
        className="relative py-28 px-6 bg-cover bg-center"
        style={{ backgroundImage: `url(${sparksImg})` }}
      >
        <div className="absolute inset-0 bg-background/85" />
        <div className="relative max-w-3xl mx-auto text-center sr" data-reveal>
          <p className="eyebrow text-foreground">Livraison & installation</p>
          <h2 className="font-serif-display text-4xl text-foreground mt-4 leading-tight">
            Un service <span className="italic text-foreground">clé en main</span>
          </h2>
          <span className="gold-rule mx-auto !w-12 my-8" />
          <p className="text-base text-muted-foreground leading-loose font-light">
            Nous offrons un service complet de location avec livraison et installation de matériel
            de sonorisation et d'éclairage. Notre équipe est à votre disposition pour vous
            conseiller sur l'utilisation optimale du matériel loué. Frais de livraison calculés
            en fonction de la distance depuis Cavaillon.
          </p>
          <Link
            to="/contactez-nous"
            className="mt-10 inline-block bg-[var(--gold)] text-[var(--ink)] px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all"
          >
            Réserver le matériel
          </Link>
        </div>
      </section>
    </div>
  );
}
